import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type FormEvent,
  type KeyboardEvent,
} from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseSelectOption, BaseSelectProps } from './types';
import { useBaseFieldControl } from './internal/baseFieldContext';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

const typeaheadResetDelay = 500;

function validateSelectProps({
  options,
  value,
  defaultValue,
  invalid,
  disabled,
  openForPreview,
  required,
}: Pick<
  BaseSelectProps,
  | 'defaultValue'
  | 'disabled'
  | 'invalid'
  | 'openForPreview'
  | 'options'
  | 'required'
  | 'value'
>) {
  for (const [name, state] of [
    ['invalid', invalid],
    ['disabled', disabled],
    ['openForPreview', openForPreview],
  ] as const) {
    if (typeof state !== 'boolean') {
      throw new TypeError(`BaseSelect ${name} must be a boolean.`);
    }
  }
  if (required !== undefined && typeof required !== 'boolean') {
    throw new TypeError('BaseSelect required must be a boolean.');
  }

  if (options !== undefined && !Array.isArray(options)) {
    throw new TypeError('BaseSelect options must be an array.');
  }
  options?.forEach((option, index) => {
    if (!option || typeof option !== 'object') {
      throw new TypeError(`BaseSelect option at index ${index} must be an object.`);
    }
    if (typeof option.value !== 'string') {
      throw new TypeError(
        `BaseSelect option value at index ${index} must be a string.`,
      );
    }
    if (
      option.disabled !== undefined &&
      typeof option.disabled !== 'boolean'
    ) {
      throw new TypeError(
        `BaseSelect option disabled at index ${index} must be a boolean.`,
      );
    }
    if (
      option.textValue !== undefined &&
      typeof option.textValue !== 'string'
    ) {
      throw new TypeError(
        `BaseSelect option textValue at index ${index} must be a string.`,
      );
    }
  });

  if (options?.length) {
    if (
      value !== undefined &&
      typeof value !== 'string' &&
      typeof value !== 'number'
    ) {
      throw new TypeError(
        'BaseSelect value must be a string or number when options are used.',
      );
    }
    if (
      defaultValue !== undefined &&
      typeof defaultValue !== 'string' &&
      typeof defaultValue !== 'number'
    ) {
      throw new TypeError(
        'BaseSelect defaultValue must be a string or number when options are used.',
      );
    }
  }
}

function normalizeValue(value: BaseSelectProps['value']) {
  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : '';
}

function findOption(options: BaseSelectOption[], value: string | undefined) {
  if (value === undefined) return undefined;
  return options.find((option) => option.value === value);
}

function getInitialActiveIndex(options: BaseSelectOption[], value: string) {
  const selectedIndex = options.findIndex(
    (option) => option.value === value && !option.disabled,
  );
  if (selectedIndex >= 0) return selectedIndex;
  return options.findIndex((option) => !option.disabled);
}

function getNextEnabledIndex(
  options: BaseSelectOption[],
  currentIndex: number,
  direction: 1 | -1,
) {
  if (!options.length) return -1;
  const startIndex = currentIndex >= 0 ? currentIndex : direction === 1 ? -1 : 0;

  for (let offset = 1; offset <= options.length; offset += 1) {
    const nextIndex =
      (startIndex + offset * direction + options.length) % options.length;
    if (!options[nextIndex]?.disabled) {
      return nextIndex;
    }
  }

  return -1;
}

function getBoundaryEnabledIndex(
  options: BaseSelectOption[],
  boundary: 'first' | 'last',
) {
  if (boundary === 'first') {
    return options.findIndex((option) => !option.disabled);
  }
  for (let index = options.length - 1; index >= 0; index -= 1) {
    if (!options[index]?.disabled) return index;
  }
  return -1;
}

function getOptionText(option: BaseSelectOption) {
  if (option.textValue !== undefined) return option.textValue;
  if (typeof option.label === 'string') return option.label;
  return option.value;
}

function toDomId(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, '-');
}

export const BaseSelect = forwardRef<HTMLSelectElement, BaseSelectProps>(
  function BaseSelect(
    {
      options,
      placeholder,
      icon,
      invalid = false,
      disabled = false,
      openForPreview = false,
      appearance,
      className,
      classNames,
      style,
      children,
      value,
      defaultValue,
      onBlur,
      onChange,
      onFocus,
      onInvalid,
      name,
      id,
      required,
      'aria-invalid': nativeAriaInvalid,
      'aria-label': nativeAriaLabel,
      'aria-labelledby': nativeAriaLabelledBy,
      'aria-describedby': nativeAriaDescribedBy,
      'aria-errormessage': nativeAriaErrorMessage,
      'aria-required': nativeAriaRequired,
      ...selectProps
    },
    ref,
  ) {
    validateSelectProps({
      options,
      value,
      defaultValue,
      invalid,
      disabled,
      openForPreview,
      required,
    });

    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const fieldControl = useBaseFieldControl();
    const resolvedInvalid = invalid || fieldControl.invalid;
    const generatedId = useId();
    const selectId = id ?? generatedId;
    const triggerId = `${selectId}-button`;
    const listboxId = `${selectId}-listbox`;
    const rootRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const selectRef = useRef<HTMLSelectElement | null>(null);
    const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const composingRef = useRef(false);
    const typeaheadBufferRef = useRef('');
    const typeaheadTimerRef = useRef<{
      ownerWindow: Window;
      timerId: number;
    } | null>(null);
    const optionList = options ?? [];
    const isCustomSelect = optionList.length > 0;
    const forceOpen = openForPreview;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const open = forceOpen || uncontrolledOpen;
    const [uncontrolledValue, setUncontrolledValue] = useState(() =>
      normalizeValue(defaultValue),
    );
    const isControlled = value !== undefined;
    const selectedValue = isControlled
      ? normalizeValue(value)
      : uncontrolledValue;
    const selectedOption = findOption(optionList, selectedValue);
    const [activeIndex, setActiveIndex] = useState(() =>
      getInitialActiveIndex(optionList, selectedValue),
    );
    const resolvedActiveIndex =
      activeIndex >= 0 && !optionList[activeIndex]?.disabled
        ? activeIndex
        : getInitialActiveIndex(optionList, selectedValue);
    const optionIds = optionList.map(
      (option, index) =>
        `${selectId}-option-${index}-${toDomId(option.value)}`,
    );
    const visibleLabel = selectedOption?.label ?? placeholder;
    const isPlaceholder = !selectedOption;
    const layerDirection = selectProps.dir ?? componentAttributes.dir;
    const rootState = disabled
      ? 'disabled'
      : resolvedInvalid
        ? 'invalid'
        : open
          ? 'open'
          : 'closed';

    const setSelectRef = (node: HTMLSelectElement | null) => {
      selectRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const handleNativeBlur = (event: FocusEvent<HTMLSelectElement>) => {
      onBlur?.(event);
      fieldControl.notifyBlur(event.currentTarget.value);
    };

    const handleNativeChange = (event: ChangeEvent<HTMLSelectElement>) => {
      onChange?.(event);
      fieldControl.notifyChange(event.currentTarget.value);
    };

    const resetActiveIndex = () => {
      setActiveIndex(getInitialActiveIndex(optionList, selectedValue));
    };

    const cancelAndClose = () => {
      resetActiveIndex();
      if (!forceOpen) setUncontrolledOpen(false);
    };

    const openMenu = (nextActiveIndex?: number) => {
      setActiveIndex(
        nextActiveIndex ?? getInitialActiveIndex(optionList, selectedValue),
      );
      if (!forceOpen) setUncontrolledOpen(true);
    };

    const notifyChange = (nextValue: string) => {
      const selectElement = selectRef.current;
      if (!selectElement) return;
      const ownerWindow = selectElement.ownerDocument.defaultView;
      if (!ownerWindow) {
        throw new Error(
          'BaseSelect cannot dispatch change without an owning window.',
        );
      }
      const valueSetter = Object.getOwnPropertyDescriptor(
        ownerWindow.HTMLSelectElement.prototype,
        'value',
      )?.set;
      if (!valueSetter) {
        throw new Error('BaseSelect could not resolve the native select value setter.');
      }
      valueSetter.call(selectElement, nextValue);
      selectElement.dispatchEvent(
        new ownerWindow.Event('change', { bubbles: true }),
      );
    };

    const commitIndex = (nextIndex: number) => {
      const option = optionList[nextIndex];
      if (!option || option.disabled || disabled) return;
      setActiveIndex(nextIndex);
      if (option.value !== selectedValue) {
        if (!isControlled) {
          setUncontrolledValue(option.value);
        }
        notifyChange(option.value);
      }
      if (!forceOpen) setUncontrolledOpen(false);
    };

    const moveActive = (nextIndex: number) => {
      if (nextIndex < 0 || optionList[nextIndex]?.disabled) return;
      setActiveIndex(nextIndex);
      if (!open) openMenu(nextIndex);
    };

    const handleTypeahead = (
      event: KeyboardEvent<HTMLButtonElement>,
      character: string,
    ) => {
      const ownerWindow = event.currentTarget.ownerDocument.defaultView;
      if (!ownerWindow) return;
      const nextBuffer = `${typeaheadBufferRef.current}${character}`.toLocaleLowerCase();
      typeaheadBufferRef.current = nextBuffer;
      if (typeaheadTimerRef.current) {
        typeaheadTimerRef.current.ownerWindow.clearTimeout(
          typeaheadTimerRef.current.timerId,
        );
      }
      typeaheadTimerRef.current = {
        ownerWindow,
        timerId: ownerWindow.setTimeout(() => {
          typeaheadBufferRef.current = '';
          typeaheadTimerRef.current = null;
        }, typeaheadResetDelay),
      };

      const repeatedCharacter = Array.from(nextBuffer).every(
        (bufferCharacter) => bufferCharacter === nextBuffer[0],
      );
      const query = repeatedCharacter ? character.toLocaleLowerCase() : nextBuffer;
      const startIndex = open
        ? resolvedActiveIndex
        : optionList.findIndex(
            (option) => option.value === selectedValue && !option.disabled,
          );
      for (let offset = 1; offset <= optionList.length; offset += 1) {
        const index = (startIndex + offset + optionList.length) % optionList.length;
        const option = optionList[index];
        if (
          !option?.disabled &&
          getOptionText(option).trim().toLocaleLowerCase().startsWith(query)
        ) {
          event.preventDefault();
          moveActive(index);
          return;
        }
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
      if (disabled || composingRef.current || event.nativeEvent.isComposing) {
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const selectedIndex = optionList.findIndex(
          (option) => option.value === selectedValue && !option.disabled,
        );
        const currentIndex = open ? resolvedActiveIndex : selectedIndex;
        moveActive(
          getNextEnabledIndex(
            optionList,
            currentIndex,
            event.key === 'ArrowDown' ? 1 : -1,
          ),
        );
        return;
      }

      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        moveActive(
          getBoundaryEnabledIndex(
            optionList,
            event.key === 'Home' ? 'first' : 'last',
          ),
        );
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (open) {
          commitIndex(resolvedActiveIndex);
        } else {
          openMenu();
        }
        return;
      }

      if (event.key === 'Escape' && open) {
        event.preventDefault();
        cancelAndClose();
        return;
      }

      if (event.key === 'Tab' && open) {
        commitIndex(resolvedActiveIndex);
        return;
      }

      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !/\s/.test(event.key)
      ) {
        handleTypeahead(event, event.key);
      }
    };

    const handleCustomFocus = (event: FocusEvent<HTMLSelectElement>) => {
      onFocus?.(event);
      if (!event.defaultPrevented) triggerRef.current?.focus();
    };

    const handleCustomInvalid = (event: FormEvent<HTMLSelectElement>) => {
      onInvalid?.(event);
      event.preventDefault();
      triggerRef.current?.focus();
    };

    useEffect(() => {
      if (!isCustomSelect || !open || forceOpen) return undefined;
      const ownerDocument = rootRef.current?.ownerDocument;
      if (!ownerDocument) return undefined;

      const handlePointerDown = (event: PointerEvent) => {
        if (!rootRef.current?.contains(event.target as Node)) {
          cancelAndClose();
        }
      };

      ownerDocument.addEventListener('pointerdown', handlePointerDown);
      return () => {
        ownerDocument.removeEventListener('pointerdown', handlePointerDown);
      };
    }, [forceOpen, isCustomSelect, open, optionList, selectedValue]);

    useEffect(() => {
      if (!isCustomSelect || !open || resolvedActiveIndex < 0) return;
      optionRefs.current[resolvedActiveIndex]?.scrollIntoView?.({
        block: 'nearest',
      });
    }, [isCustomSelect, open, resolvedActiveIndex]);

    useEffect(() => {
      if (!isCustomSelect || isControlled) return undefined;
      const selectElement = selectRef.current;
      const form = selectElement?.form;
      const ownerWindow = selectElement?.ownerDocument.defaultView;
      if (!form || !ownerWindow) return undefined;

      const handleReset = () => {
        ownerWindow.setTimeout(() => {
          const resetValue = normalizeValue(defaultValue);
          setUncontrolledValue(resetValue);
          setActiveIndex(getInitialActiveIndex(optionList, resetValue));
          if (!forceOpen) setUncontrolledOpen(false);
        }, 0);
      };
      form.addEventListener('reset', handleReset);
      return () => form.removeEventListener('reset', handleReset);
    }, [defaultValue, forceOpen, isControlled, isCustomSelect, optionList]);

    useEffect(
      () => () => {
        if (typeaheadTimerRef.current) {
          typeaheadTimerRef.current.ownerWindow.clearTimeout(
            typeaheadTimerRef.current.timerId,
          );
        }
      },
      [],
    );

    if (!isCustomSelect) {
      return (
        <div
          {...componentAttributes}
          data-monkeys-component="select"
          data-slot="root"
          data-state={rootState}
          data-native="true"
          className={cn(
            theme.slots.controlWrapper,
            resolvedInvalid && theme.slots.controlInvalid,
            disabled && theme.slots.controlDisabled,
            classNames?.root,
            className,
          )}
          style={{
            ...theme.styles.control,
            ...(resolvedInvalid ? theme.styles.controlInvalid : undefined),
            ...(disabled ? theme.styles.controlDisabled : undefined),
            ...style,
          }}
        >
          {hasRenderableNode(icon) ? (
            <span className={cn(theme.slots.controlIcon, classNames?.icon)}>
              {icon}
            </span>
          ) : null}
          <select
            {...componentAttributes}
            {...selectProps}
            id={id}
            ref={ref}
            name={name}
            value={value}
            defaultValue={defaultValue}
            required={required}
            disabled={disabled}
            aria-invalid={nativeAriaInvalid ?? (resolvedInvalid || undefined)}
            aria-label={nativeAriaLabel}
            aria-labelledby={nativeAriaLabelledBy}
            aria-describedby={nativeAriaDescribedBy}
            aria-errormessage={nativeAriaErrorMessage}
            aria-required={nativeAriaRequired}
            data-monkeys-component="select"
            data-slot="native"
            data-state={rootState}
            data-native="true"
            onBlur={handleNativeBlur}
            onFocus={onFocus}
            onInvalid={onInvalid}
            onChange={handleNativeChange}
            className={cn(
              theme.slots.controlBase,
              theme.slots.controlSelect,
              classNames?.control,
            )}
          >
            {hasRenderableNode(placeholder) ? (
              <option value="" disabled>
                {placeholder}
              </option>
            ) : null}
            {children}
          </select>
          <span className={theme.slots.selectChevron} />
        </div>
      );
    }

    return (
      <div
        {...componentAttributes}
        ref={rootRef}
        dir={layerDirection}
        data-monkeys-component="select"
        data-slot="root"
        data-state={rootState}
        data-native="false"
        className={cn(
          theme.slots.controlWrapper,
          'overflow-visible',
          open && 'z-50',
          resolvedInvalid && theme.slots.controlInvalid,
          disabled && theme.slots.controlDisabled,
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.control,
          ...(resolvedInvalid ? theme.styles.controlInvalid : undefined),
          ...(disabled ? theme.styles.controlDisabled : undefined),
          ...style,
        }}
      >
        {hasRenderableNode(icon) ? (
          <span className={cn(theme.slots.controlIcon, classNames?.icon)}>
            {icon}
          </span>
        ) : null}
        <button
          {...componentAttributes}
          ref={triggerRef}
          type="button"
          id={triggerId}
          role="combobox"
          disabled={disabled}
          dir={layerDirection}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={
            open && resolvedActiveIndex >= 0
              ? optionIds[resolvedActiveIndex]
              : undefined
          }
          aria-autocomplete="none"
          aria-invalid={nativeAriaInvalid ?? (resolvedInvalid || undefined)}
          aria-label={nativeAriaLabel}
          aria-labelledby={nativeAriaLabelledBy}
          aria-describedby={nativeAriaDescribedBy}
          aria-errormessage={nativeAriaErrorMessage}
          aria-required={nativeAriaRequired ?? (required || undefined)}
          data-monkeys-component="select"
          data-slot="trigger"
          data-state={rootState}
          className={cn(
            theme.slots.controlBase,
            theme.slots.controlSelect,
            'flex items-center text-left',
            classNames?.control,
          )}
          onClick={() => {
            if (open) cancelAndClose();
            else openMenu();
          }}
          onBlur={(event) => {
            if (!rootRef.current?.contains(event.relatedTarget)) {
              cancelAndClose();
              fieldControl.notifyBlur(selectedValue);
            }
          }}
          onCompositionStart={() => {
            composingRef.current = true;
          }}
          onCompositionEnd={() => {
            composingRef.current = false;
          }}
          onKeyDown={handleKeyDown}
        >
          <span
            className={cn(
              'min-w-0 flex-1 truncate',
              isPlaceholder && theme.slots.selectPlaceholder,
              isPlaceholder && classNames?.placeholder,
            )}
          >
            {visibleLabel}
          </span>
        </button>
        <span
          className={cn(
            theme.slots.selectChevron,
            open && 'rotate-[225deg]',
          )}
        />
        <select
          {...componentAttributes}
          {...selectProps}
          ref={setSelectRef}
          id={id}
          name={name}
          value={selectedValue}
          required={required}
          disabled={disabled}
          dir={layerDirection}
          aria-hidden="true"
          tabIndex={-1}
          data-monkeys-component="select"
          data-slot="native"
          data-state={rootState}
          data-native="false"
          className={theme.slots.selectNative}
          onBlur={onBlur}
          onFocus={handleCustomFocus}
          onInvalid={handleCustomInvalid}
          onChange={handleNativeChange}
        >
          {hasRenderableNode(placeholder) ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {optionList.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {getOptionText(option)}
            </option>
          ))}
        </select>
        {open ? (
          <div
            {...componentAttributes}
            id={listboxId}
            role="listbox"
            dir={layerDirection}
            aria-labelledby={triggerId}
            data-monkeys-component="select"
            data-slot="menu"
            data-state="open"
            className={cn(theme.slots.selectMenu, classNames?.menu)}
            style={theme.styles.selectMenu}
          >
            {optionList.map((option, index) => {
              const selected = option.value === selectedValue;
              const active = index === resolvedActiveIndex;
              const optionState = option.disabled
                ? 'disabled'
                : active
                  ? 'active'
                  : selected
                    ? 'selected'
                    : 'idle';
              return (
                <button
                  {...componentAttributes}
                  ref={(node) => {
                    optionRefs.current[index] = node;
                  }}
                  key={option.value}
                  id={optionIds[index]}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  disabled={option.disabled}
                  dir={layerDirection}
                  aria-selected={selected}
                  data-monkeys-component="select"
                  data-slot="option"
                  data-state={optionState}
                  data-active={active || undefined}
                  data-selected={selected || undefined}
                  data-disabled={option.disabled || undefined}
                  className={cn(
                    theme.slots.selectOption,
                    active && theme.slots.selectOptionActive,
                    selected && theme.slots.selectOptionSelected,
                    option.disabled && theme.slots.selectOptionDisabled,
                    classNames?.option,
                  )}
                  style={{
                    ...theme.styles.selectOption,
                    ...(active ? theme.styles.selectOptionActive : undefined),
                    ...(selected ? theme.styles.selectOptionSelected : undefined),
                  }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => commitIndex(index)}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  },
);
